import { apiService } from './ApiService';
import { OrderTicket } from '../models/OrderTicket';
import { OrderRequest } from '../models/OrderRequest';
import { Store } from '../wrappers';
const EMPTY_RESULT = { newTicketHashes: [], newRequestIds: [] };
/**
 * Single-flight guard: app boot fires fetchSnapshot from two places at once
 * (App.vue tenant-level hydration — which also covers KDS terminals — and
 * MainLayout's initial poll). Concurrent callers share one HTTP request and
 * one store mutation; both receive the same result, so MainLayout's alerts
 * still fire exactly once.
 */
let inFlight = null;
// Delta-poll watermark, sourced from the server's clock (the `server_time` of
// the previous snapshot), never from local ticket updated_at. A terminal with
// a skewed clock would otherwise push a locally-derived watermark into the
// server's future and exclude every server-side change until it caught up —
// on the exact channel that backstops missed Pusher pushes. Module-scoped, so
// an app restart starts from a full hydration.
let lastServerWatermark = null;
async function fetchSnapshotExec() {
    const params = {};
    if (lastServerWatermark)
        params.last_ticket_updated_at = lastServerWatermark;
    try {
        const response = await apiService.get('/orders-hub', { params });
        // Advance the watermark to the server's clock for the next delta poll.
        if (response.data.server_time)
            lastServerWatermark = response.data.server_time;
        const orderTickets = response.data.order_tickets ?? [];
        const orderRequests = response.data.order_requests ?? [];
        // 1. OrderTicket — incremental upsert (additive by primary key).
        // With a watermark the backend returns ALL statuses changed since,
        // including terminal-state transitions (rejects, completions) this
        // terminal missed live — callers must not treat every new hash as
        // an alert-worthy active ticket.
        const knownTicketHashes = new Set(OrderTicket.all().map(t => t.hash));
        // Merge each record rather than blind-upsert: the watermark doesn't
        // protect locally-ahead tickets (optimistic saves don't bump
        // updated_at), so a stale snapshot row could regress status or wipe a
        // local completion backfill.
        orderTickets.forEach(t => OrderTicket.mergeServerRecord(t));
        const newTicketHashes = orderTickets
            .map(t => t.hash)
            .filter(h => !knownTicketHashes.has(h));
        // 2. OrderRequest — GC by absence then upsert. The backend returns only
        // requests still needing action (pending, or approved-but-unlinked after
        // a lost approve response); rejected and order-linked requests are
        // excluded, so a local row absent from the snapshot has been handled and
        // is pruned. Snapshot the local collection before deleting: $delete
        // mutates the backing store, so iterating OrderRequest.all() live is unsafe.
        const incomingIds = new Set(orderRequests.map(r => r.id));
        const localRequests = OrderRequest.all();
        const knownRequestIds = new Set(localRequests.map(r => r.id));
        for (const local of localRequests) {
            if (!incomingIds.has(local.id)) {
                local.$delete();
            }
        }
        OrderRequest.insert({ data: orderRequests });
        // Only pending requests are alert-worthy; an approved-but-unlinked row
        // (recovery case) is new to this terminal but must not re-toast.
        const newRequestIds = orderRequests
            .filter(r => r.status === 'pending' && !knownRequestIds.has(r.id))
            .map(r => r.id);
        return { newTicketHashes, newRequestIds };
    }
    catch {
        return EMPTY_RESULT;
    }
}
const OrdersHubService = {
    /**
     * Single source of truth for fetching the Orders Hub snapshot.
     *
     * Hydrates both local stores from `GET /orders-hub`:
     *  - OrderTicket: incremental upsert via the `last_ticket_updated_at`
     *    watermark. `OrderTicket.insert` is additive (upsert by primary key),
     *    so locally-created tickets not in the response survive untouched.
     *  - OrderRequest: full-replacement GC — the response is authoritative for
     *    pending requests, so any local row absent from it is deleted before
     *    upserting the incoming set.
     *
     * Single-flight: concurrent callers share one request (see `inFlight`).
     * Returns the hashes/ids of newly-discovered items so callers can raise
     * alerts — note delta responses include terminal-state tickets, so filter
     * to active ones before alerting. Returns empty arrays (no-op) when
     * offline, when no tenant is logged in, or on any failure.
     */
    async fetchSnapshot() {
        // Requires a logged-in tenant: without a tenant token the `/orders-hub`
        // request would go out unauthenticated (e.g. on the tenant-login screen).
        if (!Store.get('isOnline') || !Store.get('tenant')) {
            return EMPTY_RESULT;
        }
        if (inFlight)
            return inFlight;
        inFlight = fetchSnapshotExec();
        try {
            return await inFlight;
        }
        finally {
            inFlight = null;
        }
    },
};
export default OrdersHubService;
export { OrdersHubService };
//# sourceMappingURL=OrdersHubService.js.map